import React, { useState, useEffect } from "react";
import { useGlobalContext } from "../../context/stateContext";
import { useAuth } from "../../context/Auth";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Loading from "../../components/Loading/Loading";
import Swal from "sweetalert2";
import {
  Package,
  Building2,
  DollarSign,
  TrendingDown,
  ArrowLeft,
  Save,
  AlertCircle,
} from "lucide-react";

const BranchDrugReorderLevel = () => {
  const { inventory_id } = useParams();
  const { requestWithAuth } = useAuth();
  const { activeSession } = useGlobalContext();
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [reorderLevel, setReorderLevel] = useState(null);
  const [drugDetails, setDrugDetails] = useState(null);

  const fetchDrugDetails = async () => {
    setLoading(true);
    try {
      const data = await requestWithAuth(
        `/inventory/branch/${inventory_id}/item/`,
        "GET"
      );
      setDrugDetails(data);
      setReorderLevel(data?.reorder_level);
    } catch (error) {
      console.error("Error fetching users data:", error);
      toast.error("Failed to get inventory detail");
    } finally {
      setLoading(false);
    }
  };

  const updateReorderLevel = (e) => {
    e.preventDefault();
    if (!reorderLevel || reorderLevel <= 0) {
      toast.error("Please enter a valid reorder level");
      return;
    }
    handleUpdate();
  };

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      await requestWithAuth(
        `/inventory/branch/${inventory_id}/item/`,
        "PUT",
        {
          reorder_level: reorderLevel,
        },
        {
          "Content-Type": "application/json",
        }
      );
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Reorder level updated successfully",
      }).then(() => {
        window.history.back();
      });
    } catch (error) {
      toast.error("Failed to update reorder level");
    } finally {
      setUpdating(false);
    }
  };

  useEffect(() => {
    fetchDrugDetails();
  }, []);

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>Inventory</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">
                  Edit Reorder Level
                </span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <TrendingDown size={32} className="text-blue-600" />
                Edit Reorder Level
              </h1>
            </div>
            <button
              onClick={() => window.history.back()}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <ArrowLeft size={18} />
              Back
            </button>
          </div>
        </div>

        {/* Main Content - Centered */}
        <div className="max-w-3xl mx-auto">
          {/* Drug Information Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Package className="text-blue-600" size={20} />
                <h2 className="text-lg font-semibold text-gray-900">
                  Item Details
                </h2>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Drug Name */}
                <div>
                  <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                    <Package size={14} />
                    Drug Name
                  </label>
                  <input
                    type="text"
                    disabled
                    value={drugDetails?.drug?.name || ""}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm bg-gray-50 text-gray-700"
                  />
                </div>

                {/* Branch Name */}
                <div>
                  <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                    <Building2 size={14} />
                    Branch Name
                  </label>
                  <input
                    type="text"
                    disabled
                    value={drugDetails?.branch?.name || ""}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm bg-gray-50 text-gray-700"
                  />
                </div>

                {/* Selling Price */}
                <div>
                  <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                    <DollarSign size={14} />
                    Selling Price
                  </label>
                  <input
                    type="text"
                    disabled
                    value={`GH₵ ${parseFloat(
                      drugDetails?.drug?.selling_price || 0
                    ).toFixed(2)}`}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm bg-gray-50 text-gray-700 font-medium"
                  />
                </div>

                {/* Current Quantity */}
                {drugDetails?.quantity !== undefined && (
                  <div>
                    <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                      <Package size={14} />
                      Current Quantity
                    </label>
                    <input
                      type="text"
                      disabled
                      value={drugDetails?.quantity || 0}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm bg-gray-50 text-gray-700 font-medium"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Reorder Level Edit Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-red-50">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <AlertCircle className="text-orange-600" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Update Reorder Level
                  </h2>
                  <p className="text-sm text-gray-600 mt-0.5">
                    Set the minimum stock quantity before reordering is required
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="space-y-6">
                {/* Reorder Level Input */}
                <div>
                  <label
                    htmlFor="reorder-level"
                    className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2"
                  >
                    <TrendingDown size={16} className="text-gray-500" />
                    Reorder Level
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="reorder-level"
                    type="number"
                    min="0"
                    onChange={(e) => setReorderLevel(e.target.value)}
                    value={reorderLevel || ""}
                    required
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    placeholder="Enter reorder level..."
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    When stock falls below this level, you'll receive a low
                    stock alert
                  </p>
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle size={18} className="text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-blue-900">
                        Important Information
                      </p>
                      <p className="text-xs text-blue-700 mt-1">
                        Setting an appropriate reorder level helps prevent
                        stockouts and ensures continuous availability of this
                        item. Consider factors like lead time, demand patterns,
                        and storage capacity.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Card Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => window.history.back()}
                  className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                {updating ? (
                  <button
                    type="button"
                    disabled
                    className="bg-blue-400 text-white rounded-lg px-6 py-2.5 flex items-center gap-2 cursor-not-allowed"
                  >
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Updating...
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={updateReorderLevel}
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-6 py-2.5 font-medium transition-colors shadow-sm flex items-center gap-2"
                  >
                    <Save size={18} />
                    Save Changes
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BranchDrugReorderLevel;
